import React, { useEffect, useState, useRef } from "react";
import { auth, db } from "./firebaseConfig";
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";


const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("");
  const [teacherCode, setTeacherCode] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    // Get dark mode preference from localStorage or default to false
    const savedMode = localStorage.getItem("darkMode");
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const [performanceData, setPerformanceData] = useState([]);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isActivatingTeachers, setIsActivatingTeachers] = useState(false);
  const [activationMessage, setActivationMessage] = useState(null);
  const particlesRef = useRef(null);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  
  // Educational quotes collection
  const educationalQuotes = [
    {
      text: "The function of education is to teach one to think intensively and to think critically. Intelligence plus character — that is the goal of true education.",
      author: "Martin Luther King Jr."
    },
    {
      text: "Education is the most powerful weapon which you can use to change the world.",
      author: "Nelson Mandela"
    },
    {
      text: "The beautiful thing about learning is that nobody can take it away from you.",
      author: "B.B. King"
    },
    {
      text: "Education is not the filling of a pail, but the lighting of a fire.",
      author: "William Butler Yeats"
    },
    {
      text: "Live as if you were to die tomorrow. Learn as if you were to live forever.",
      author: "Mahatma Gandhi"
    },
    {
      text: "The mind is not a vessel to be filled, but a fire to be kindled.",
      author: "Plutarch"
    },
    {
      text: "Education is not preparation for life; education is life itself.",
      author: "John Dewey"
    }
  ];

  // Check for reduced motion preference
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setReducedMotion(prefersReducedMotion);
  }, []);
  
  // Quote carousel effect
  useEffect(() => {
    if (!reducedMotion) {
      const interval = setInterval(() => {
        setCurrentQuoteIndex((prevIndex) => 
          prevIndex === educationalQuotes.length - 1 ? 0 : prevIndex + 1
        );
      }, 8000); // Change quote every 8 seconds
      
      return () => clearInterval(interval);
    }
  }, [reducedMotion, educationalQuotes.length]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("darkMode", JSON.stringify(newMode));
  };

  // Generate a random code for teacher
  const generateRandomCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 3; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  // Fetch username and role from Firestore
  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.uid) {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUsername(data.username || data.displayName || user.email);
            setRole(data.role || "student");
            
            // Handle teacher code for teachers
            if (data.role === "teacher") {
              if (data.teacherCode) {
                setTeacherCode(data.teacherCode);
              } else {
                // Generate teacher code if it doesn't exist
                const name = (data.username || data.displayName || "").split(" ")[0].toUpperCase();
                const randomCode = generateRandomCode();
                const newTeacherCode = `AI-${name}-${randomCode}`;
                
                // Update Firestore with the new teacher code
                try {
                  await updateDoc(userDocRef, { teacherCode: newTeacherCode });
                  setTeacherCode(newTeacherCode);
                } catch (error) {
                  console.error("Error updating teacher code:", error.message);
                }
              }
            }
          } else {
            console.error("User document does not exist");
          }
        } catch (error) {
          console.error("Error fetching user data:", error.message);
        }
      }
    };
    fetchUserData();
  }, [user]);

  // Handle copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(teacherCode)
      .then(() => {
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  // Handle activation of teacher accounts
  const handleActivateTeacherAccounts = async () => {
    setIsActivatingTeachers(true);
    setActivationMessage(null);
    
    try {
      const functions = getFunctions();

      const activateTeacherAccounts = httpsCallable(functions, 'activateTeacherAccounts');
      const result = await activateTeacherAccounts();
      
      setActivationMessage({
        type: 'success',
        text: result.data.message || '✅ All teacher accounts activated!'
      });
    } catch (error) {
      console.error('Error activating teacher accounts:', error);
      setActivationMessage({
        type: 'error',
        text: `❌ Error: ${error.message}`
      });
    } finally {
      setIsActivatingTeachers(false);
    }
  };

  // Handlers
  const handleLogout = () => {
    auth
      .signOut()
      .then(() => {
        console.log("User signed out successfully.");
        navigate("/");
      })
      .catch((error) => {
        console.error("Error signing out:", error.message);
      });
  };

  const navigationItems = {
    admin: [
      { label: "Manage Users", path: "/manage-users", icon: "👥" },
      { label: "Create Questions", path: "/create-questions", icon: "❓" },
      { label: "Create Quiz", path: "/create-quiz", icon: "📝" },
      { label: "View Quizzes", path: "/view-quizzes", icon: "📊" },
      { label: "View Answers", path: "/view-quiz-responses", icon: "📋" },
      { label: "AutoReport", path: "/auto-report", icon: "📄" }
    ],
  teacher: [
    { label: "Create Questions", path: "/create-questions", icon: "❓" },
    { label: "Create Quiz", path: "/create-quiz", icon: "📝" },
    { label: "View Quizzes", path: "/view-quizzes", icon: "📊" },
    { label: "View Answers", path: "/view-quiz-responses", icon: "📋" },
    { label: "View Students", path: "/view-students", icon: "👨‍🎓" },
    { label: "AutoReport", path: "/auto-report", icon: "📄" }
  ],
    student: [
      { label: "Join Teacher", path: "/join-teacher", icon: "👨‍🏫" },
      { label: "Enter Quiz Code", path: "/enter-quiz", icon: "🔑" },
      { label: "View My Scores", path: "/view-my-scores", icon: "🏆" },
      { label: "Typing Test", path: "/typing-test", icon: "⌨️" }
    ]
  };

  // Fetch performance data for teachers
  useEffect(() => {
    const fetchPerformanceData = async () => {
      if (role === "teacher" || role === "admin") {
        try {
          // Get all quizzes created by this teacher
          const quizzesRef = collection(db, "quizzes");
          const quizzesQuery = query(quizzesRef, where("createdBy", "==", user.uid));
          const quizzesSnapshot = await getDocs(quizzesQuery);
          
          if (quizzesSnapshot.empty) return;
          
          const quizData = [];
          
          for (const quizDoc of quizzesSnapshot.docs) {
            const quiz = quizDoc.data();
            
            // Get submissions for this quiz
            const submissionsRef = collection(db, "submissions");
            const submissionsQuery = query(submissionsRef, where("quizCode", "==", quiz.quizCode));
            const submissionsSnapshot = await getDocs(submissionsQuery);
            
            if (submissionsSnapshot.empty) continue;
            
            // Calculate average score
            let totalScore = 0;
            let totalMaxScore = 0;
            let submissionCount = 0;
            
            submissionsSnapshot.docs.forEach(subDoc => {
              const submission = subDoc.data();
              if (submission.markAwarded && submission.markAwarded !== "Pending") {
                totalScore += parseFloat(submission.markAwarded);
                submissionCount++;
              }
            });
            
            const averageScore = submissionCount > 0 ? (totalScore / submissionCount).toFixed(2) : 0;
            
            quizData.push({
              name: quiz.title || `Quiz ${quiz.quizCode}`,
              averageScore: parseFloat(averageScore),
              submissions: submissionCount
            });
          }
          
          setPerformanceData(quizData);
        } catch (error) {
          console.error("Error fetching performance data:", error);
        }
      }
    };
    
    if (user?.uid && (role === "teacher" || role === "admin")) {
      fetchPerformanceData();
    }
  }, [user, role]);

  // Role-based theme colors with dark mode support
  const themeColors = {
    admin: {
      primary: darkMode ? "#b455d3" : "#8e44ad",
      secondary: darkMode ? "#c67de0" : "#9b59b6",
      light: darkMode ? "#382a3d" : "#d6b5e5",
      dark: "#2d1a36",
      gradient: darkMode 
        ? "linear-gradient(135deg, #6a2f80 0%, #8a3ca3 100%)"
        : "linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)"
    },
    teacher: {
      primary: darkMode ? "#3498db" : "#2980b9",
      secondary: darkMode ? "#5dade2" : "#3498db",
      light: darkMode ? "#1e3c4e" : "#c2dcf2",
      dark: "#172b39",
      gradient: darkMode 
        ? "linear-gradient(135deg, #1a5889 0%, #2a7ab9 100%)"
        : "linear-gradient(135deg, #2980b9 0%, #3498db 100%)"
    },
    student: {
      primary: darkMode ? "#2ecc71" : "#27ae60",
      secondary: darkMode ? "#58d68d" : "#2ecc71",
      light: darkMode ? "#1e3d2f" : "#c8f7d6",
      dark: "#173b25",
      gradient: darkMode 
        ? "linear-gradient(135deg, #1d8047 0%, #25a25a 100%)"
        : "linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)"
    }
  };

  const currentTheme = themeColors[role] || themeColors.student;

  // Animation for particles
  useEffect(() => {
    if (reducedMotion) return;
    
    const canvas = particlesRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const particles = [];
    const particleCount = 50;
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Create particles
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 3 + 1,
        color: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)',
        speedX: Math.random() * 0.5 - 0.25,
        speedY: Math.random() * 0.5 - 0.25
      });
    }
    
    // Animation loop
    let animationFrameId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        // Move particles
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.speedX = -particle.speedX;
        }
        
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.speedY = -particle.speedY;
        }
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();
      });
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Update particle colors when dark mode changes
    particles.forEach(particle => {
      particle.color = darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)';
    });
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [darkMode, reducedMotion]);

  // Dynamic styles with dark mode support
  const styles = {
    pageContainer: {
      position: "relative",
      minHeight: "100vh",
      backgroundImage: darkMode ? "none" : "url('/green1.jpg')",
      backgroundColor: darkMode ? "#121212" : "transparent",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed",
      fontFamily: "'Poppins', 'Segoe UI', sans-serif",
      transition: "background-color 0.3s ease, color 0.3s ease",
      color: darkMode ? "#e0e0e0" : "#333",
    },
    canvas: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: 0,
      opacity: reducedMotion ? 0 : 1,
    },
    contentWrapper: {
      position: "relative",
      zIndex: 1,
      minHeight: "100vh",
      backgroundColor: darkMode ? "rgba(18, 18, 18, 0.7)" : "rgba(255, 255, 255, 0.25)",
      backdropFilter: "blur(5px)",
      transition: "background-color 0.3s ease",
    },
    header: {
      background: currentTheme.gradient,
      color: "white",
      padding: "1rem 2rem",
      boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "sticky",
      top: 0,
      zIndex: 100,
      transition: "background 0.3s ease, box-shadow 0.3s ease",
    },
    logoContainer: {
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
    },
    logoImage: {
      width: "36px",
      height: "36px",
      borderRadius: "8px",
    },
    logoText: {
      fontSize: "1.5rem",
      fontWeight: "600",
      margin: 0,
    },
    navContainer: {
      display: "flex",
      alignItems: "center",
      gap: "1rem",
    },
    userInfo: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: "0.5rem 1rem",
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      borderRadius: "50px",
      maxWidth: "200px",
      marginRight: "1rem",
    },
    
    darkModeToggle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      cursor: "pointer",
      transition: "background-color 0.3s ease",
      marginRight: "1rem",
    },
    
    toggleIcon: {
      fontSize: "1.2rem",
      color: "white",
      transition: "transform 0.3s ease",
    },
    avatar: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      backgroundColor: "white",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: currentTheme.primary,
      fontWeight: "600",
    },
    username: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: "0.9rem",
    },
    mobileMenuButton: {
      background: "rgba(255, 255, 255, 0.2)",
      border: "none",
      color: "white",
      fontSize: "1.5rem",
      cursor: "pointer",
      padding: "0.5rem",
      borderRadius: "8px",
      width: "40px",
      height: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "background-color 0.3s ease",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.3)",
      },
    },
    navButtons: {
      display: "flex",
      alignItems: "center",
    },
    mobileMenu: {
      display: isMenuOpen ? "flex" : "none",
      position: "absolute",
      top: "100%",
      right: "0",
      backgroundColor: darkMode ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.95)",
      boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
      borderRadius: "8px",
      padding: "1rem",
      flexDirection: "column",
      gap: "0.75rem",
      zIndex: 100,
      margin: "0.5rem",
      maxHeight: "80vh",
      overflowY: "auto",
      width: "250px",
      backdropFilter: "blur(5px)",
    },
    navButton: {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      border: "none",
      color: "white",
      padding: "0.5rem 1rem",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontWeight: "500",
      whiteSpace: "nowrap",
      fontSize: "0.9rem",
      "@media (max-width: 1024px)": {
        padding: "0.4rem 0.8rem",
        fontSize: "0.85rem",
      },
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.3)",
      },
    },
    logoutButton: {
      backgroundColor: "rgba(231, 76, 60, 0.8)",
      border: "none",
      color: "white",
      padding: "0.5rem 1rem",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontWeight: "500",
      whiteSpace: "nowrap",
      fontSize: "0.9rem",
      "@media (max-width: 1024px)": {
        padding: "0.4rem 0.8rem",
        fontSize: "0.85rem",
      },
      "&:hover": {
        backgroundColor: "rgba(231, 76, 60, 1)",
      },
    },
    mainContent: {
      padding: "2rem",
      maxWidth: "1200px",
      margin: "0 auto",
    },
    welcomeCard: {
      background: darkMode ? "rgba(30, 30, 30, 0.9)" : "rgba(255, 255, 255, 0.9)",
      borderRadius: "16px",
      padding: "2rem",
      marginBottom: "2rem",
      boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
      textAlign: "center",
      backdropFilter: "blur(10px)",
      transition: "background-color 0.3s ease, box-shadow 0.3s ease",
    },
    welcomeTitle: {
      color: currentTheme.primary,
      fontSize: "1.8rem",
      marginBottom: "0.5rem",
      fontWeight: "600",
      transition: "color 0.3s ease",
    },
    roleTag: {
      display: "inline-block",
      padding: "0.25rem 1rem",
      backgroundColor: currentTheme.light,
      color: currentTheme.primary,
      borderRadius: "50px",
      fontSize: "0.9rem",
      fontWeight: "600",
      marginTop: "0.5rem",
      textTransform: "capitalize",
    },
    quoteBanner: {
      backgroundColor: darkMode ? "rgba(40, 40, 40, 0.7)" : "rgba(255, 255, 255, 0.7)",
      borderRadius: "12px",
      padding: "1.5rem",
      marginTop: "1.5rem",
      textAlign: "center",
      color: currentTheme.primary,
      fontStyle: "italic",
      border: `1px solid ${currentTheme.light}`,
      transition: "background-color 0.3s ease, border 0.3s ease, color 0.3s ease",
    },
    quoteText: {
      fontSize: "1.1rem",
      marginBottom: "0.5rem",
    },
    quoteAuthor: {
      fontWeight: "600",
      fontSize: "0.9rem",
    },
    featureGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
      gap: "1.5rem",
      marginTop: "2rem",
    },
    // Base style for feature cards (hover effect will be applied via component state)
    performanceCard: {
      background: darkMode ? "rgba(30, 30, 30, 0.9)" : "rgba(255, 255, 255, 0.9)",
      borderRadius: "16px",
      padding: "2rem",
      marginBottom: "2rem",
      boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
      backdropFilter: "blur(10px)",
      transition: "background-color 0.3s ease, box-shadow 0.3s ease",
    },
    featureCard: {
      backgroundColor: darkMode ? "rgba(30, 30, 30, 0.9)" : "rgba(255, 255, 255, 0.9)",
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.2)" : "0 4px 12px rgba(0, 0, 0, 0.05)",
      transition: "transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease",
      cursor: "pointer",
      height: "100%",
      backdropFilter: "blur(5px)",
    },
    featureIconContainer: {
      backgroundColor: darkMode ? currentTheme.dark : currentTheme.light,
      color: currentTheme.primary,
      padding: "1.5rem",
      textAlign: "center",
      fontSize: "2.5rem",
      transition: "background-color 0.3s ease, color 0.3s ease",
    },
    featureContent: {
      padding: "1.5rem",
    },
    featureTitle: {
      fontSize: "1.2rem",
      fontWeight: "600",
      color: darkMode ? "#e0e0e0" : "#333",
      marginBottom: "0.5rem",
      transition: "color 0.3s ease",
    },
    featureDescription: {
      color: darkMode ? "#aaa" : "#666",
      fontSize: "0.9rem",
      lineHeight: "1.5",
      transition: "color 0.3s ease",
    },
    footer: {
      textAlign: "center",
      padding: "2rem",
      color: "#fff",
      fontSize: "0.9rem",
      backgroundColor: darkMode ? "rgba(0, 0, 0, 0.8)" : "rgba(0, 0, 0, 0.6)",
      marginTop: "2rem",
      transition: "background-color 0.3s ease",
    },
  };

  const featureDescriptions = {
    "Manage Users": "Add, edit, or remove user accounts and manage their roles and permissions.",
    "Create Questions": "Design and build new questions for your quizzes with various formats.",
    "Create Quiz": "Compile questions into quizzes and generate access codes for students.",
    "View Quizzes": "Browse through all available quizzes and their statistics.",
    "View Answers": "Review student submissions and their automatically marked responses.",
    "View Students": "View and manage students who have joined your class using your teacher code.",
    "Join Teacher": "Enter a teacher's code to join their class and access their quizzes.",
    "Enter Quiz Code": "Access assigned quizzes using the unique code provided by your teacher.",
    "View My Scores": "Track your performance across different quizzes and assessments.",
    "Typing Test": "Practice and improve your typing speed and accuracy with timed tests.",
    "AutoReport": "Generate personalized student reports using shared and individual data."
  };

  // Utility to get initials for avatar
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // New FeatureCard component with hover state
  const FeatureCard = ({ item }) => {
    const [hover, setHover] = useState(false);

    // Merge base style with hover-dependent styles
    const cardStyle = {
      ...styles.featureCard,
      transform: hover ? "translateY(-5px)" : "none",
      boxShadow: hover
        ? "0 12px 24px rgba(0, 0, 0, 0.1)"
        : "0 4px 12px rgba(0, 0, 0, 0.05)",
    };

    return (
      <Link 
        to={item.path}
        style={{ textDecoration: 'none' }}
      >
        <div
          style={cardStyle}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <div style={styles.featureIconContainer}>{item.icon}</div>
          <div style={styles.featureContent}>
            <h3 style={styles.featureTitle}>{item.label}</h3>
            <p style={styles.featureDescription}>
              {featureDescriptions[item.label]}
            </p>
          </div>
        </div>
      </Link>
    );
  };

  // Render feature cards for current role
  const renderFeatureCards = () => {
    return navigationItems[role]?.map((item, index) => (
      <FeatureCard key={index} item={item} />
    ));
  };

  return (
    <div style={styles.pageContainer}>
      <canvas ref={particlesRef} style={styles.canvas} />
      <div style={styles.contentWrapper}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.logoContainer}>
            <img src="/newlogo.png" alt="Logo" style={styles.logoImage} />
            <h1 style={styles.logoText}>AI Automarker</h1>
          </div>
          <div style={styles.navContainer}>
            <div style={styles.userInfo}>
              <div style={styles.avatar}>
                {getInitials(username || user?.displayName || "User")}
              </div>
              <span style={styles.username}>
                {username || user?.displayName || "User"}
              </span>
            </div>
            <div 
              style={styles.darkModeToggle} 
              onClick={toggleDarkMode}
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              <span style={styles.toggleIcon}>
                {darkMode ? "☀️" : "🌙"}
              </span>
            </div>
            <button
              style={styles.mobileMenuButton}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              ☰
            </button>
            <div style={styles.navButtons}>
              <button style={styles.logoutButton} onClick={handleLogout}>
                Logout
              </button>
            </div>
            {isMenuOpen && (
              <div style={styles.mobileMenu}>
                <Link to="/dashboard" style={{ textDecoration: 'none', width: '100%' }}>
                  <button style={{
                    ...styles.navButton,
                    width: '100%',
                    textAlign: 'left',
                    backgroundColor: darkMode ? "rgba(40, 40, 40, 0.7)" : "rgba(255, 255, 255, 0.3)",
                    color: darkMode ? "#e0e0e0" : "#333",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = darkMode ? "rgba(52, 152, 219, 0.3)" : "rgba(52, 152, 219, 0.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = darkMode ? "rgba(40, 40, 40, 0.7)" : "rgba(255, 255, 255, 0.3)"}
                  >
                    Dashboard
                  </button>
                </Link>
                {navigationItems[role]?.map((item, index) => (
                  <Link key={index} to={item.path} style={{ textDecoration: 'none', width: '100%' }}>
                    <button style={{
                      ...styles.navButton,
                      width: '100%',
                      textAlign: 'left',
                      backgroundColor: darkMode ? "rgba(40, 40, 40, 0.7)" : "rgba(255, 255, 255, 0.3)",
                      color: darkMode ? "#e0e0e0" : "#333",
                      transition: "background-color 0.3s ease",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = darkMode ? "rgba(52, 152, 219, 0.3)" : "rgba(52, 152, 219, 0.1)"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = darkMode ? "rgba(40, 40, 40, 0.7)" : "rgba(255, 255, 255, 0.3)"}
                    >
                      {item.icon} {item.label}
                    </button>
                  </Link>
                ))}
                <button 
                  style={{
                    ...styles.logoutButton,
                    width: '100%',
                    textAlign: 'left',
                    backgroundColor: darkMode ? "rgba(231, 76, 60, 0.7)" : "rgba(231, 76, 60, 0.7)",
                    color: "white",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(231, 76, 60, 0.9)"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = darkMode ? "rgba(231, 76, 60, 0.7)" : "rgba(231, 76, 60, 0.7)"}
                  onClick={handleLogout}
                >
                  🚪 Logout
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main style={styles.mainContent}>
          <div style={styles.welcomeCard}>
            <h2 style={styles.welcomeTitle}>
              Welcome, {username || user?.displayName || "User"}!
            </h2>
            <div style={styles.roleTag}>{role}</div>
            <div style={styles.quoteBanner}>
              <div style={{
                position: 'relative',
                height: '100%',
                overflow: 'hidden',
                transition: reducedMotion ? 'none' : 'opacity 1s ease-in-out'
              }}>
                <p style={styles.quoteText}>
                  "{educationalQuotes[currentQuoteIndex].text}"
                </p>
                <p style={styles.quoteAuthor}>— {educationalQuotes[currentQuoteIndex].author}</p>
                
                {/* Quote navigation dots */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '1rem',
                  gap: '0.5rem'
                }}>
                  {educationalQuotes.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentQuoteIndex(index)}
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: index === currentQuoteIndex ? currentTheme.primary : (darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'),
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'background-color 0.3s ease'
                      }}
                      aria-label={`Quote ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Teacher Code Display */}
          {role === "teacher" && (
            <div style={{
              ...styles.welcomeCard,
              marginBottom: "2rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}>
              <h3 style={{
                color: currentTheme.primary,
                fontSize: "1.5rem",
                marginBottom: "1rem",
                fontWeight: "600"
              }}>
                Teacher Code
              </h3>
              <p style={{
                fontSize: "1rem",
                marginBottom: "1.5rem",
                color: darkMode ? "#aaa" : "#666",
                textAlign: "center"
              }}>
                Share this code with your students to allow them to join your class
              </p>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                maxWidth: "400px",
                position: "relative"
              }}>
                <input
                  type="text"
                  value={teacherCode}
                  readOnly
                  style={{
                    padding: "1rem",
                    fontSize: "1.2rem",
                    fontWeight: "600",
                    backgroundColor: darkMode ? "rgba(40, 40, 40, 0.7)" : "rgba(245, 245, 245, 0.7)",
                    color: currentTheme.primary,
                    border: `2px solid ${currentTheme.light}`,
                    borderRadius: "8px",
                    width: "100%",
                    textAlign: "center",
                    cursor: "pointer",
                    outline: "none"
                  }}
                  onClick={copyToClipboard}
                />
                <button
                  onClick={copyToClipboard}
                  style={{
                    position: "absolute",
                    right: "10px",
                    backgroundColor: "transparent",
                    border: "none",
                    color: currentTheme.primary,
                    fontSize: "1.2rem",
                    cursor: "pointer",
                    padding: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  title="Copy to clipboard"
                >
                  📋
                </button>
                {showCopiedMessage && (
                  <div style={{
                    position: "absolute",
                    bottom: "-30px",
                    backgroundColor: darkMode ? "rgba(40, 40, 40, 0.9)" : "rgba(0, 0, 0, 0.7)",
                    color: "#fff",
                    padding: "0.5rem 1rem",
                    borderRadius: "4px",
                    fontSize: "0.9rem"
                  }}>
                    Copied to clipboard!
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Admin Actions Card */}
          {role === "admin" && (
            <div style={{
              ...styles.welcomeCard,
              marginBottom: "2rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}>
              <h3 style={{
                color: currentTheme.primary,
                fontSize: "1.5rem",
                marginBottom: "1rem",
                fontWeight: "600"
              }}>
                Admin Actions
              </h3>
              <p style={{
                fontSize: "1rem",
                marginBottom: "1.5rem",
                color: darkMode ? "#aaa" : "#666",
                textAlign: "center"
              }}>
                Perform administrative tasks for the entire system
              </p>
              
              <button 
                onClick={handleActivateTeacherAccounts}
                disabled={isActivatingTeachers}
                style={{
                  padding: "0.75rem 1.5rem",
                  fontSize: "1rem",
                  fontWeight: "600",
                  backgroundColor: isActivatingTeachers 
                    ? (darkMode ? "rgba(100, 100, 100, 0.7)" : "rgba(200, 200, 200, 0.7)") 
                    : currentTheme.primary,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: isActivatingTeachers ? "not-allowed" : "pointer",
                  transition: "background-color 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}
              >
                {isActivatingTeachers ? (
                  <>
                    <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⏳</span>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>🔄</span>
                    <span>Activate Teacher Accounts</span>
                  </>
                )}
              </button>
              
              {activationMessage && (
                <div style={{
                  marginTop: "1.5rem",
                  padding: "1rem",
                  borderRadius: "8px",
                  backgroundColor: activationMessage.type === "success" 
                    ? (darkMode ? "rgba(46, 204, 113, 0.2)" : "rgba(46, 204, 113, 0.1)")
                    : (darkMode ? "rgba(231, 76, 60, 0.2)" : "rgba(231, 76, 60, 0.1)"),
                  color: activationMessage.type === "success" ? "#2ecc71" : "#e74c3c",
                  border: `1px solid ${activationMessage.type === "success" ? "#2ecc71" : "#e74c3c"}`,
                  width: "100%",
                  maxWidth: "500px",
                  textAlign: "center"
                }}>
                  {activationMessage.text}
                </div>
              )}
            </div>
          )}
          
          {/* Performance Data Table for Teachers */}
          {(role === "teacher" || role === "admin") && performanceData.length > 0 && (
            <div style={styles.performanceCard}>
              <h3 style={{
                color: currentTheme.primary,
                fontSize: "1.5rem",
                marginBottom: "1.5rem",
                fontWeight: "600",
                textAlign: "center",
                transition: "color 0.3s ease",
              }}>
                Class Performance Overview
              </h3>
              
              <div style={{
                width: "100%",
                overflowX: "auto",
              }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: "1rem",
                  backgroundColor: darkMode ? "rgba(40, 40, 40, 0.7)" : "rgba(255, 255, 255, 0.7)",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        padding: "1rem",
                        textAlign: "left",
                        borderBottom: `2px solid ${currentTheme.primary}`,
                        color: darkMode ? "#e0e0e0" : "#333",
                      }}>Quiz Name</th>
                      <th style={{
                        padding: "1rem",
                        textAlign: "center",
                        borderBottom: `2px solid ${currentTheme.primary}`,
                        color: darkMode ? "#e0e0e0" : "#333",
                      }}>Average Score</th>
                      <th style={{
                        padding: "1rem",
                        textAlign: "center",
                        borderBottom: `2px solid ${currentTheme.primary}`,
                        color: darkMode ? "#e0e0e0" : "#333",
                      }}>Submissions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performanceData.map((item, index) => (
                      <tr key={index} style={{
                        backgroundColor: index % 2 === 0 
                          ? (darkMode ? "rgba(50, 50, 50, 0.5)" : "rgba(245, 245, 245, 0.5)") 
                          : (darkMode ? "rgba(40, 40, 40, 0.5)" : "rgba(255, 255, 255, 0.5)"),
                      }}>
                        <td style={{
                          padding: "0.75rem 1rem",
                          borderBottom: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                          color: darkMode ? "#e0e0e0" : "#333",
                        }}>{item.name}</td>
                        <td style={{
                          padding: "0.75rem 1rem",
                          textAlign: "center",
                          borderBottom: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                          color: darkMode ? "#e0e0e0" : "#333",
                          fontWeight: "600",
                        }}>{item.averageScore}</td>
                        <td style={{
                          padding: "0.75rem 1rem",
                          textAlign: "center",
                          borderBottom: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                          color: darkMode ? "#e0e0e0" : "#333",
                        }}>{item.submissions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          <div style={styles.featureGrid}>{renderFeatureCards()}</div>
        </main>
        <footer style={styles.footer}>
          <p>© {new Date().getFullYear()} AI Automarker. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default Dashboard;
